import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { accountCopy } from '@/copy'
import { Alert } from '@/ui/Alert/Alert'
import { Button } from '@/ui/Button/Button'
import { EmptyState } from '@/ui/EmptyState/EmptyState'
import { TextField } from '@/ui/Field/Field'
import { Icon } from '@/ui/Icon/Icon'
import { Skeleton } from '@/ui/Loading/Loading'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { ProgressBar } from '@/ui/ProgressBar/ProgressBar'
import { RowList, RowListItem } from '@/ui/RowList/RowList'
import { SectionHeader } from '@/ui/SectionHeader/SectionHeader'
import { StatusBadge, StatusDot, type StatusTone } from '@/ui/Status/Status'
import {
  type BillingAccount,
  createBillingAccount,
  createCheckoutSession,
  createPortalSession,
  getBillingAccount,
  linkWorkspace,
  listBillingAccounts,
} from '../../api/billing'
import { RefreshFailed } from '../../components/RefreshFailed'
import { useMeQuery } from '../../hooks/queries'
import { useToastStore } from '../../store/toast'
import { formatUsd } from '../../utils/format'
import styles from './BillingTab.module.css'

const { billing } = accountCopy

// ── Helpers ───────────────────────────────────────────────────────────────────

function msToDaysRemaining(ms: number | null): number | null {
  if (ms === null) return null
  return Math.max(0, Math.ceil((ms - Date.now()) / (1000 * 60 * 60 * 24)))
}

const BILLING_STATUS_TONE: Record<
  BillingAccount['status'],
  { label: string; tone: StatusTone }
> = {
  trialing: { label: billing.status.trialing, tone: 'info' },
  active: { label: billing.status.active, tone: 'success' },
  past_due: { label: billing.status.past_due, tone: 'warning' },
  canceled: { label: billing.status.canceled, tone: 'danger' },
  incomplete: { label: billing.status.incomplete, tone: 'warning' },
  unpaid: { label: billing.status.unpaid, tone: 'danger' },
}

function BillingStatusBadge({ status }: { status: BillingAccount['status'] }) {
  const { label, tone } = BILLING_STATUS_TONE[status] ?? {
    label: status,
    tone: 'neutral' as const,
  }
  return <StatusBadge tone={tone}>{label}</StatusBadge>
}

// ── Account detail view ───────────────────────────────────────────────────────

function BillingAccountView({ accountId }: { accountId: string }) {
  const { addToast } = useToastStore()
  const qc = useQueryClient()

  const {
    data: account,
    isPending,
    isError: refreshFailed,
    refetch,
  } = useQuery({
    queryKey: ['billing', accountId],
    queryFn: () => getBillingAccount(accountId),
    refetchInterval: 30_000,
  })
  const showSkeleton = useSkeletonDelay(isPending)

  const { data: me } = useMeQuery()

  const checkoutMut = useMutation({
    mutationFn: () => createCheckoutSession(accountId),
    onSuccess: ({ url }) => {
      window.location.href = url
    },
    onError: () => addToast(billing.checkoutFailed, 'error'),
  })

  const portalMut = useMutation({
    mutationFn: () => createPortalSession(accountId),
    onSuccess: ({ url }) => {
      window.location.href = url
    },
    onError: () => addToast(billing.portalFailed, 'error'),
  })

  const linkMut = useMutation({
    mutationFn: (workspaceId: string) => linkWorkspace(accountId, workspaceId),
    onSuccess: () => {
      addToast(billing.workspaceLinked, 'success')
      void qc.invalidateQueries({ queryKey: ['billing', accountId] })
    },
    onError: () => addToast(billing.linkFailed, 'error'),
  })

  if (!account) {
    return (
      <div aria-busy={isPending}>
        {isPending ? (
          showSkeleton && <Skeleton lines={4} />
        ) : (
          <Alert tone="danger">
            <Inline gap="space-8" align="baseline" wrap>
              <Text>{billing.loadFailed}</Text>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void refetch()}
              >
                {accountCopy.retry}
              </Button>
            </Inline>
          </Alert>
        )}
      </div>
    )
  }

  const daysLeft = msToDaysRemaining(account.trial_ends_at)
  const creditPct =
    account.trial_credits_usd > 0
      ? Math.min(
          100,
          (account.trial_spent_usd / account.trial_credits_usd) * 100,
        )
      : 0
  const unlinkedWorkspaces = (me?.workspaces ?? []).filter(
    (w) => !account.workspaces.includes(w.workspace_id),
  )

  return (
    <Stack gap="space-24">
      {refreshFailed && (
        <RefreshFailed
          message={billing.refreshFailed}
          onRetry={() => void refetch()}
        />
      )}
      <Inline justify="between" align="start" gap="space-12">
        <Stack gap="space-4">
          <Text as="h2" weight="strong">
            {account.name}
          </Text>
          <Inline gap="space-8">
            <BillingStatusBadge status={account.status} />
            {account.status === 'trialing' && daysLeft !== null && (
              <Text variant="caption" tone="text-mute" numeric>
                {daysLeft === 0
                  ? billing.expiresToday
                  : billing.daysLeft(daysLeft)}
              </Text>
            )}
          </Inline>
        </Stack>
        {!account.has_subscription && (
          <Button
            onClick={() => checkoutMut.mutate()}
            loading={checkoutMut.isPending}
          >
            {billing.upgrade}
          </Button>
        )}
        {account.has_subscription && (
          <Button
            variant="ghost"
            onClick={() => portalMut.mutate()}
            loading={portalMut.isPending}
          >
            <Icon name="gear" size="icon-embedded" />
            {billing.manage}
          </Button>
        )}
      </Inline>

      {(account.status === 'past_due' || account.status === 'unpaid') && (
        <Alert tone="warning">{billing.overdue}</Alert>
      )}
      {account.status === 'canceled' && (
        <Alert tone="danger">{billing.canceled}</Alert>
      )}

      {account.status === 'trialing' && (
        <section>
          <SectionHeader rule trailingSpacing="section">
            {billing.trialUsage}
          </SectionHeader>
          <Stack gap="space-12">
            <ProgressBar
              className={styles.meter}
              label={billing.creditsUsed}
              value={account.trial_spent_usd}
              max={account.trial_credits_usd || 1}
              valueLabel={`${formatUsd(account.trial_spent_usd)} / ${formatUsd(account.trial_credits_usd)}`}
              tone={
                creditPct >= 100
                  ? 'danger'
                  : creditPct >= 70
                    ? 'warning'
                    : 'accent'
              }
              message={
                creditPct >= 100
                  ? billing.creditsExhausted
                  : creditPct >= 70
                    ? billing.creditsLow
                    : undefined
              }
            />
          </Stack>
        </section>
      )}

      <section>
        <SectionHeader rule trailingSpacing="section">
          {billing.currentPeriod}
        </SectionHeader>
        <Stack gap="space-12">
          <RowList hover={false} className={styles.meter}>
            <RowListItem>
              <Text tone="text-mute" className={styles.label}>
                {billing.periodSpend}
              </Text>
              <Text numeric>{formatUsd(account.period_spent_usd)}</Text>
            </RowListItem>
          </RowList>
        </Stack>
      </section>

      <section>
        <SectionHeader rule trailingSpacing="section">
          {billing.workspaces}
        </SectionHeader>
        <Stack gap="space-12">
          <Text as="p" tone="text-mute">
            {billing.workspacesHelp}
          </Text>
          <RowList aria-label={billing.linkedList}>
            {account.workspaces.length === 0 && (
              <RowListItem>
                <EmptyState variant="inline" description={billing.noneLinked} />
              </RowListItem>
            )}
            {account.workspaces.map((id) => (
              <RowListItem key={id}>
                <Text tone="text-sub">
                  <StatusDot tone="success" label={billing.linked} />
                </Text>
                <Text variant="code" tone="text-mute">
                  {id}
                </Text>
              </RowListItem>
            ))}
            {unlinkedWorkspaces.map((w) => (
              <RowListItem key={w.workspace_id}>
                <Text tone="text-mute" className={styles.label}>
                  {w.name}
                </Text>
                <Button
                  variant="ghost"
                  onClick={() => linkMut.mutate(w.workspace_id)}
                  loading={
                    linkMut.isPending && linkMut.variables === w.workspace_id
                  }
                >
                  {billing.link}
                </Button>
              </RowListItem>
            ))}
          </RowList>
        </Stack>
      </section>
    </Stack>
  )
}

// ── Create account form ───────────────────────────────────────────────────────

function CreateAccountForm({ onCreated }: { onCreated: (id: string) => void }) {
  const [name, setName] = useState('')
  const { addToast } = useToastStore()

  const mut = useMutation({
    mutationFn: () => createBillingAccount(name.trim()),
    onSuccess: (account) => onCreated(account.id),
    onError: () => addToast(billing.create.failed, 'error'),
  })

  return (
    <Stack gap="space-16">
      <Text as="p" tone="text-mute">
        {billing.create.intro}
      </Text>
      <TextField
        id="billing-name"
        label={billing.create.name}
        value={name}
        onValue={setName}
        inputProps={{ placeholder: billing.create.namePlaceholder }}
      />
      <Inline gap="space-8">
        <Button
          onClick={() => mut.mutate()}
          disabled={!name.trim()}
          loading={mut.isPending}
        >
          {billing.create.submit}
        </Button>
      </Inline>
    </Stack>
  )
}

// ── Tab ───────────────────────────────────────────────────────────────────────

export function BillingTab() {
  const [activeAccountId, setActiveAccountId] = useState<string | null>(null)

  // Read ?success=1 once on mount and strip it from the URL so it doesn't
  // persist across navigation or refreshes. Other params (e.g. ?tab=) are
  // preserved.
  // A return from checkout is a transient result: a success toast, once.
  const [searchParams, setSearchParams] = useSearchParams()
  const addToast = useToastStore((s) => s.addToast)
  const toasted = useRef(false)
  useEffect(() => {
    if (!searchParams.has('success')) return
    if (searchParams.get('success') === '1' && !toasted.current) {
      toasted.current = true
      addToast(billing.activated, 'success')
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete('success')
        return next
      },
      { replace: true },
    )
  }, [searchParams, setSearchParams, addToast])

  const {
    data: accounts,
    isPending,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['billing', 'accounts'],
    queryFn: listBillingAccounts,
  })
  const showSkeleton = useSkeletonDelay(isPending)

  // Auto-select the first account once loaded, unless the user already picked one.
  const firstAccountId = accounts?.[0]?.id ?? null
  const resolvedAccountId = activeAccountId ?? firstAccountId

  return (
    <Stack gap="space-24">
      {isError && accounts !== undefined && (
        <RefreshFailed
          message={billing.listRefreshFailed}
          onRetry={() => void refetch()}
        />
      )}
      <div aria-busy={isPending}>
        {isPending ? (
          showSkeleton && <Skeleton lines={4} />
        ) : isError && accounts === undefined ? (
          <Alert tone="danger">
            <Inline gap="space-8" align="baseline" wrap>
              <Text>{billing.listFailed}</Text>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void refetch()}
              >
                {accountCopy.retry}
              </Button>
            </Inline>
          </Alert>
        ) : resolvedAccountId ? (
          <BillingAccountView accountId={resolvedAccountId} />
        ) : (
          <CreateAccountForm onCreated={setActiveAccountId} />
        )}
      </div>
    </Stack>
  )
}
